/**
 * Scam Shield (§23) — Family Anti-Fraud.
 *
 * Privacy-respecting: the parent (or child) actively pastes a suspicious
 * message or link and gets a calm 3-step verdict + what-to-do advice. Never
 * covert monitoring. Also one-tap "report scam" and a verified-entity list.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ShieldCheck, ShieldAlert, ShieldX, Link2, MessageSquare, Flag } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { scamApi, type ScanVerdict, type Verdict } from '@/api/scam';
import { PageHeader } from './PageHeader';

const VERDICT_STYLE: Record<Verdict, { label: string; cls: string; Icon: typeof ShieldCheck }> = {
  safe: { label: 'Looks safe', cls: 'bg-emerald-50 text-emerald-800 border-emerald-200', Icon: ShieldCheck },
  suspicious: { label: 'Suspicious', cls: 'bg-amber-50 text-amber-900 border-amber-200', Icon: ShieldAlert },
  high_risk: { label: 'High-risk scam', cls: 'bg-rose-50 text-rose-800 border-rose-200', Icon: ShieldX },
};

function VerdictView({ v }: { v: ScanVerdict }) {
  const s = VERDICT_STYLE[v.verdict];
  return (
    <div className={`mt-3 rounded-card border p-4 ${s.cls}`}>
      <div className="flex items-center gap-2">
        <s.Icon className="size-5" />
        <p className="text-sm font-semibold">{s.label}</p>
        <span className="ml-auto text-xs opacity-70">risk {v.score}/100</span>
      </div>
      <p className="mt-2 text-sm">{v.advice}</p>
      {v.reasons.length > 0 && (
        <ul className="mt-2 space-y-1">
          {v.reasons.map((r, i) => (
            <li key={i} className="flex gap-2 text-xs"><span>•</span>{r}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function ScamShieldPage() {
  const { t } = useTranslation();
  return (
    <div className="px-6 py-8 md:px-10 md:py-10">
      <PageHeader
        title={t('pages.scam_shield.title')}
        icon={ShieldAlert}
        tint="green"
        description={t('pages.scam_shield.description')}
      />
      <Tabs defaultValue="message" className="mt-6">
        <TabsList>
          <TabsTrigger value="message">Check a message</TabsTrigger>
          <TabsTrigger value="link">Check a link</TabsTrigger>
          <TabsTrigger value="report">Report a scam</TabsTrigger>
          <TabsTrigger value="trusted">Verified senders</TabsTrigger>
        </TabsList>
        <TabsContent value="message"><MessageScanner /></TabsContent>
        <TabsContent value="link"><LinkScanner /></TabsContent>
        <TabsContent value="report"><ReportForm /></TabsContent>
        <TabsContent value="trusted"><VerifiedList /></TabsContent>
      </Tabs>
    </div>
  );
}

function MessageScanner() {
  const [text, setText] = useState('');
  const scan = useMutation({ mutationFn: () => scamApi.scanMessage(text) });
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base"><MessageSquare className="size-4" /> Suspicious message scanner</CardTitle>
        <CardDescription>Paste a text from any messenger (Armenian, Russian, English).</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <textarea
          className="min-h-28 w-full rounded-md border border-[color:var(--kz-color-border-subtle)] bg-transparent p-3 text-sm"
          placeholder="Paste the suspicious message here…"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <Button onClick={() => scan.mutate()} disabled={text.trim().length < 1 || scan.isPending}>
          {scan.isPending ? 'Checking…' : 'Check message'}
        </Button>
        {scan.data && <VerdictView v={scan.data} />}
      </CardContent>
    </Card>
  );
}

function LinkScanner() {
  const [url, setUrl] = useState('');
  const scan = useMutation({ mutationFn: () => scamApi.scanLink(url) });
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base"><Link2 className="size-4" /> Safe Link Guardian</CardTitle>
        <CardDescription>Check a link before opening it — we look for impersonation, shorteners and disguises.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Input placeholder="https://…" value={url} onChange={(e) => setUrl(e.target.value)} />
        <Button onClick={() => scan.mutate()} disabled={url.trim().length < 4 || scan.isPending}>
          {scan.isPending ? 'Checking…' : 'Check link'}
        </Button>
        {scan.data && <VerdictView v={scan.data} />}
      </CardContent>
    </Card>
  );
}

function ReportForm() {
  const [sender, setSender] = useState('');
  const [link, setLink] = useState('');
  const [note, setNote] = useState('');
  const report = useMutation({
    mutationFn: () => scamApi.report({ sender: sender || undefined, link: link || undefined, note: note || undefined }),
    onSuccess: () => { toast.success('Reported. Thank you — this helps protect other families.'); setSender(''); setLink(''); setNote(''); },
    onError: () => toast.error('Could not submit the report.'),
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base"><Flag className="size-4" /> Report a scam</CardTitle>
        <CardDescription>One tap to report. Reports are reviewed before influencing shared threat intel.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Input placeholder="Sender (phone / email / handle)" value={sender} onChange={(e) => setSender(e.target.value)} />
        <Input placeholder="Suspicious link (optional)" value={link} onChange={(e) => setLink(e.target.value)} />
        <textarea className="min-h-20 w-full rounded-md border border-[color:var(--kz-color-border-subtle)] bg-transparent p-3 text-sm" placeholder="Notes (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
        <Button onClick={() => report.mutate()} disabled={report.isPending}>{report.isPending ? 'Reporting…' : 'Report scam'}</Button>
      </CardContent>
    </Card>
  );
}

function VerifiedList() {
  const entities = useQuery({ queryKey: ['scam', 'verified'], queryFn: () => scamApi.verifiedEntities() });
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Verified senders</CardTitle>
        <CardDescription>Official banks, government bodies and operators. If a message claims to be one of these but the link doesn't match, treat it as a scam.</CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="divide-y divide-[color:var(--kz-color-border-subtle)]">
          {(entities.data ?? []).map((e) => (
            <li key={e.id} className="flex items-center gap-3 py-2.5 text-sm">
              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-800">{e.category}</span>
              <span className="flex-1 font-medium">{e.name}</span>
              <span className="font-mono text-xs text-[color:var(--kz-color-ink-muted)]">{e.domains.join(', ')}</span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
